import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionTitle } from "../ui";
import { ReplyBox } from "../ReplyBox";

const STATUS_STYLE = {
  confirmed: "bg-sage-light text-sage-700",
  pending_payment: "bg-amber-100 text-amber-700",
  cancelled: "bg-charcoal-100 text-charcoal-500",
};

export default function Bookings() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState({});
  const [replyOpen, setReplyOpen] = useState({});

  const load = () => api.get("/admin/bookings").then((res) => setRows(res.data));
  useEffect(() => { load(); }, []);

  const save = async (id) => {
    await api.put(`/admin/bookings/${id}`, edit[id] || {});
    toast.success("Booking updated");
    setEdit((e) => ({ ...e, [id]: undefined }));
    load();
  };

  return (
    <div data-testid="admin-bookings">
      <SectionTitle title="Bookings" subtitle="Paid consultations, payment status, and meeting details." />
      <div className="space-y-4">
        {rows.length === 0 && <p className="text-sm text-charcoal-500">No bookings yet.</p>}
        {rows.map((b) => {
          const e = edit[b.id] || {};
          return (
            <div key={b.id} data-testid={`booking-${b.id}`} className="rounded-xl bg-white p-6 border border-sand-200 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-charcoal-800">{b.name} <span className="text-charcoal-400 font-normal">· {b.email}</span></p>
                  <p className="text-sm text-charcoal-500 mt-1">
                    {new Date(b.slot.includes("Z") ? b.slot : b.slot + ":00").toLocaleString()} · {b.platform} · ${b.amount}
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLE[b.status] || "bg-sand-100"}`}>
                  {b.status} · {b.payment_status}
                  {b.replied && <span className="ml-2 rounded-full bg-sage/20 px-2 py-0.5 text-[10px] uppercase tracking-wide text-sage-700">replied</span>}
                </span>
              </div>
              {b.pricing_selection?.package_name && (
                <div data-testid={`booking-package-${b.id}`} className="mt-3 rounded-lg bg-terracotta/10 border border-terracotta/30 px-3 py-2">
                  <p className="text-xs font-medium text-terracotta">
                    Pricing page selection: {b.pricing_selection.package_name}
                    {b.pricing_selection.direction && <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-charcoal-700" data-testid={`booking-direction-${b.id}`}>{b.pricing_selection.direction}</span>}
                    {b.pricing_selection.estimate && <> · est. {b.pricing_selection.estimate}</>}
                  </p>
                  {b.pricing_selection.addons?.length > 0 && (
                    <p className="mt-0.5 text-xs text-charcoal-600">Add-ons: {b.pricing_selection.addons.join(", ")}</p>
                  )}
                </div>
              )}
              {b.notes && <p className="mt-3 text-sm text-charcoal-600 rounded-lg bg-sand-50 p-3">Client note: {b.notes}</p>}
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <input
                  data-testid={`booking-link-${b.id}`}
                  placeholder="Meeting link"
                  defaultValue={b.meeting_link}
                  onChange={(ev) => setEdit((p) => ({ ...p, [b.id]: { ...p[b.id], meeting_link: ev.target.value } }))}
                  className="bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
                <input
                  data-testid={`booking-notes-${b.id}`}
                  placeholder="Admin notes"
                  defaultValue={b.admin_notes}
                  onChange={(ev) => setEdit((p) => ({ ...p, [b.id]: { ...p[b.id], admin_notes: ev.target.value } }))}
                  className="bg-white border border-sand-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
              </div>
              <button
                data-testid={`booking-save-${b.id}`}
                onClick={() => save(b.id)}
                disabled={!edit[b.id]}
                className="mt-3 rounded-full bg-terracotta px-5 py-2 text-sm text-white font-medium hover:bg-terracotta-hover transition-colors disabled:opacity-50"
              >
                Save
              </button>
              <button
                data-testid={`booking-reply-toggle-${b.id}`}
                onClick={() => setReplyOpen((p) => ({ ...p, [b.id]: !p[b.id] }))}
                className="mt-3 ml-2 rounded-full border border-sand-300 px-5 py-2 text-sm text-charcoal-600 hover:bg-sand-100 transition-colors"
              >
                {replyOpen[b.id] ? "Hide reply" : `Reply${(b.replies || []).length ? ` (${b.replies.length})` : ""}`}
              </button>
              {replyOpen[b.id] && (
                <ReplyBox email={b.email} replies={b.replies} endpoint={`/admin/bookings/${b.id}/reply`} onSent={load} testId={`booking-${b.id}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
